import { signOut } from 'firebase/auth';
import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { endEmrSessionOnPageHide } from '@/api/adapters/emr';
import { auth } from '@/api/firebase';
import { queryClient } from '@/app/providers/queryClient';
import { useAuthStore } from '@/app/store/auth.store';
import { ShcLogo } from '@/components/brand/ShcLogo';
import { useLogout } from '@/shared/hooks/use-logout';

/** Local sign-out: the in-memory Firebase user, auth state and every cached patient read. */
function clearLocal() {
  void signOut(auth);
  useAuthStore.getState().logout();
  queryClient.clear();
}

interface EmrShellProps {
  children?: ReactNode;
}

/**
 * Shell for a session opened from the hospital EMR (SHCT-994, plan D20). Same dark bar as the GNB but with no menu
 * tabs, no notification bell and no user menu: the GNB fetches notifications on mount, which an EMR session may not
 * call (api-contracts/emr-bridge.md §B.1.1). The only action is [종료], which also ends the session server-side.
 */
export function EmrShell({ children }: EmrShellProps) {
  const { t } = useTranslation(['common', 'medical']);
  const name = useAuthStore((s) => s.name);
  const performLogout = useLogout();

  // Closing the window or reloading ends the session too (plan D9④·D21).
  useEffect(() => endEmrSessionOnPageHide(), []);

  // pagehide already ended the session server-side; a page restored from the back-forward cache must not show the
  // patient again from memory.
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) clearLocal();
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  const handleEnd = () => {
    // Ending must never strand the doctor in a live session, so a failed server call still clears locally.
    performLogout().catch(clearLocal);
  };

  return (
    <>
      <header className="gnb gnb--v3 gnb--dark">
        <div className="gnb__left">
          <span className="gnb__logo">
            <div className="gnb__logo-icon" aria-hidden="true">
              <ShcLogo size={18} />
            </div>
            {t('common:gnb.brand')}
          </span>
          <span className="badge badge-warning">{t('medical:emr.shell.badge')}</span>
        </div>
        <div className="gnb__right">
          <span className="gnb__emr-name">{name}</span>
          <button type="button" className="btn btn--secondary btn--sm" onClick={handleEnd}>
            {t('medical:emr.shell.end')}
          </button>
        </div>
      </header>
      <div className="content-wrapper content-wrapper--v3">
        <main id="content">{children}</main>
      </div>
    </>
  );
}
